/* eslint-disable no-console */
// Vinculación de un movimiento bancario a 1..N facturas SII, cada factura saldada por sus CUOTAS
// (una o varias) o por un MONTO manual (facturas sin plan). Fuente ÚNICA de escritura del flujo
// "Pago/Cobro SII" del modal (10.1 / 10.103): reemplaza los dos caminos históricos —single con una
// cuota (handleConfirmar) y múltiple sin cuotas (handleConfirmarMultiple)— por una sola lógica que
// no depende de cuántas facturas se eligen.
//
// Modelo de datos (SIN migración de esquema — decisión tomada):
//   - El vínculo AUTORITATIVO cuota↔movimiento es `plan_pagos_facturas.movimiento_bancario_id`
//     (N→1: varias cuotas pueden colgar del mismo movimiento). Lo escribe este util por cada cuota.
//   - `movimientos_bancarios.plan_pago_cuota_id` / `plan_cobro_cuota_id` son ESCALARES y no pueden
//     representar N cuotas: este util NO los escribe nunca (los deja en null). Los motores de cobro
//     (getCobroFactura Camino 1 MBF / getFacturasYaVinculadas Fuente 3) resuelven la cobranza por
//     Σ `movimiento_bancario_facturas.monto`, que este util deja correcto (una fila por factura con
//     el monto APLICADO a esa factura, no el débito entero — fix del bug del puente).
//   - FK escalar de factura (`factura_sii_recibida_id`/`emitida_id`): B3 Fase B — ya NO se escribe (ni
//     single ni multi). La vinculación vive SIEMPRE en la tabla puente MBF; el estado de cobro lo
//     resuelve `getCobroReal` por el puente. (`fkEscalar` en el return sigue indicando "fue single";
//     el caller lo usa solo para el embed OPTIMISTA del badge, no para escribir la FK.)
//
// El cuadre TOTAL (Σ montos aplicados vs |débito|) reusa `evaluarCuadreCuotas` (10.103). Multi-moneda
// por la FECHA del movimiento vía `convertirAMonedaLocal` (10.185). Sin fallo silencioso (10.177):
// cada escritura chequea su `{ error }`. `supabaseClient` se inyecta (default al singleton) para test.

import { evaluarCuadreCuotas } from "./cuadreCuotas";
import { TOLERANCIA_REDONDEO_LOCAL } from "./estadoCuenta";
import { convertirAMonedaLocal } from "./exchangeRateService";
import { getLineaIdPorPosicion } from "./lineasFacturacion/getLineaId";
import { client } from "./supabase";

const monedaLocalDe = (pais) => (pais === "Perú" ? "PEN" : "CLP");

// Cuotas tildadas de una factura seleccionada (shape del modal). [] si no hay plan / no se tildó nada.
const cuotasDe = (fSel) => (Array.isArray(fSel?.cuotasSeleccionadas) ? fSel.cuotasSeleccionadas : []);

// Monto aplicado a UNA factura (en la moneda de la factura):
//   - con cuotas tildadas → Σ monto_cuota de las cuotas tildadas (derivado, NO editable).
//   - sin plan → montoAsignado (manual). La UI SIEMPRE lo pre-carga para facturas sin plan.
//   - con plan pero SIN cuotas tildadas → 0 (NUNCA monto_total): así el cuadre no pasa por defecto
//     y no se puede confirmar una factura con plan sin elegir cuotas (evita cuotas huérfanas).
// Es el monto que va a la fila MBF de esa factura (fix del puente: antes iba el débito entero).
export const montoAplicadoFactura = (fSel) => {
  const cuotas = cuotasDe(fSel);
  if (cuotas.length > 0) return cuotas.reduce((s, c) => s + Math.abs(Number(c.monto_cuota || 0)), 0);
  return Number(fSel?.montoAsignado != null ? fSel.montoAsignado : 0);
};

// Moneda de una factura para comparar/convertir: la recibida (pago) siempre es local; la emitida
// (cobro) hereda su propia `moneda` (puede ser USD en DTE 110).
const monedaFacturaDe = (fSel, esCobro, pais) => (esCobro ? fSel?.moneda || monedaLocalDe(pais) : monedaLocalDe(pais));

// Cuadre TOTAL del movimiento contra la Σ de montos aplicados (una entrada por factura), en moneda
// local por la FECHA del movimiento (10.185). Reusa evaluarCuadreCuotas pasando las facturas como
// "conjunto de cuotas" con montoDe = monto aplicado convertido. Tolerancia = redondeo (10) escalado
// por la cantidad de cuotas tildadas (mínimo 1 por factura, como el single).
//   - asignado:   Σ montos aplicados (local).
//   - montoMovCmp: |débito| en local.
//   - sinAsignar: montoMovCmp − asignado, CON signo (>0 falta asignar / <0 sobre-asignado).
//   - cuadra:     |sinAsignar| ≤ tolerancia.
export const evaluarCuadreAplicaciones = ({ facturasSeleccionadas = [], movimiento, esCobro, pais }) => {
  const local = monedaLocalDe(pais);
  const monedaMov = movimiento?.moneda || local;
  const montoMovCmp =
    monedaMov !== local
      ? convertirAMonedaLocal(Math.abs(Number(movimiento?.monto || 0)), monedaMov, pais, movimiento?.fecha)
      : Math.abs(Number(movimiento?.monto || 0));

  const montoDe = (fSel) => {
    const aplicado = montoAplicadoFactura(fSel);
    const monedaFac = monedaFacturaDe(fSel, esCobro, pais);
    return monedaFac !== local ? convertirAMonedaLocal(aplicado, monedaFac, pais, movimiento?.fecha) : aplicado;
  };

  const nCuotas = facturasSeleccionadas.reduce((n, f) => n + Math.max(cuotasDe(f).length, 1), 0);
  const tolerancia = Math.max(nCuotas, 1) * TOLERANCIA_REDONDEO_LOCAL;

  const {
    sumaCuotas: asignado,
    diferencia,
    cuadra,
  } = evaluarCuadreCuotas(facturasSeleccionadas, montoMovCmp, tolerancia, montoDe);
  return { asignado, montoMovCmp, sinAsignar: diferencia, cuadra, moneda: local, tolerancia };
};

// Neto / impuesto de la caja consolidada, prorrateados por el monto aplicado de cada factura
// (montoAplicado / monto_total). Con cuotas parciales el neto no se sobre-declara; con factura
// completa el ratio es 1 y coincide con el total. En moneda cruda de cada factura (la caja hereda
// la moneda del movimiento; el desglose neto/impuesto es informativo).
const netoImpuestoConsolidado = (facturasSeleccionadas) => {
  let neto = 0;
  let impuesto = 0;
  for (const f of facturasSeleccionadas) {
    const total = Number(f.monto_total || 0);
    const ratio = total > 0 ? montoAplicadoFactura(f) / total : 1;
    neto += Number(f.monto_neto || 0) * ratio;
    impuesto += Number(f.monto_iva || 0) * ratio;
  }
  return { neto: Math.round(neto), impuesto: Math.round(impuesto) };
};

// ── FASE 4 (dual-write) — espejo de la vinculación al libro `aplicaciones_saldo` ─────────────────
// Escribe un evento `via='banco'` por CADA aplicación (una cuota tildada, o UNA aplicación manual si
// la factura no tiene plan), reflejando lo mismo que las filas MBF/cuota del write autoritativo. NO
// reemplaza nada (Fase 5); es un espejo adicional que resolverSaldo(documento) suma. Solo SII.

// Congela la conversión de UNA aplicación (10.185): monto_documento en la moneda del documento;
// monto_origen en la moneda en que se movió la plata (la del movimiento).
const congelarConversionEvento = ({ montoDoc, monedaFac, monedaMov, local, pais, fecha }) => {
  if (monedaMov === monedaFac)
    return { monto_origen: montoDoc, moneda_origen: monedaFac, tc: null, fecha_tc: null, warn: null };
  if (monedaMov === local) {
    // Documento en moneda extranjera (ej. emitida USD) y pago en moneda local: convertir el monto del
    // documento a local y congelar el TC de la fecha del movimiento.
    const enLocal = convertirAMonedaLocal(montoDoc, monedaFac, pais, fecha);
    return {
      monto_origen: enLocal,
      moneda_origen: local,
      tc: montoDoc ? enLocal / montoDoc : null,
      fecha_tc: fecha,
      warn: null,
    };
  }
  // Caso raro (pago en una moneda extranjera distinta a la del documento): el conversor solo va a
  // local → se degrada a la moneda del documento (resolverSaldo sigue correcto por monto_documento)
  // y se marca para que NO pase en silencio (10.177/10.170).
  return {
    monto_origen: montoDoc,
    moneda_origen: monedaFac,
    tc: null,
    fecha_tc: null,
    warn: `cross-currency no soportado por el conversor (mov ${monedaMov} / doc ${monedaFac}); evento congelado en la moneda del documento`,
  };
};

// Construye (puro, testeable) los eventos de `aplicaciones_saldo` de una vinculación: un evento por
// cuota tildada (cuota_id + monto_cuota) o UNO por factura sin plan (cuota_id null + montoAsignado).
// eje pago=recibida / cobro=emitida; via='banco'; origen = el movimiento. Σ eventos por documento =
// monto aplicado (= la fila MBF) → resolverSaldo da el mismo saldo que la lógica vieja.
// `documentoTipo` (opcional) fuerza el tipo del documento (F1-B1: 'boleta_honorario'); sin él se
// deriva de esCobro (sii_emitida / sii_recibida). El `eje` se deriva SIEMPRE del tipo — igual que el
// CHECK `chk_aplicaciones_saldo_eje_tipo` (emitida→cobro, resto→pago). Para boletas, la moneda del
// documento es local (CLP): `monedaFacturaDe(f, esCobro=false, pais)` ya la da correcta.
export const construirEventosSaldo = ({
  movimiento,
  facturasSeleccionadas = [],
  esCobro,
  pais,
  usuarioId = null,
  documentoTipo = null,
}) => {
  const local = monedaLocalDe(pais);
  const monedaMov = movimiento?.moneda || local;
  const documento_tipo = documentoTipo || (esCobro ? "sii_emitida" : "sii_recibida");
  const eje = documento_tipo === "sii_emitida" ? "cobro" : "pago";
  const eventos = [];
  const warnings = [];
  for (const f of facturasSeleccionadas) {
    const monedaFac = monedaFacturaDe(f, esCobro, pais);
    const cuotas = cuotasDe(f);
    const aplicaciones =
      cuotas.length > 0
        ? cuotas.map((c) => ({ cuota_id: c.id, montoDoc: Math.abs(Number(c.monto_cuota || 0)) }))
        : [{ cuota_id: null, montoDoc: Number(f.montoAsignado != null ? f.montoAsignado : 0) }];
    for (const a of aplicaciones) {
      if (!(a.montoDoc > 0)) continue; // no sembrar aplicaciones de monto 0
      const conv = congelarConversionEvento({
        montoDoc: a.montoDoc,
        monedaFac,
        monedaMov,
        local,
        pais,
        fecha: movimiento.fecha,
      });
      if (conv.warn) warnings.push(`factura ${f.id}: ${conv.warn}`);
      eventos.push({
        documento_tipo,
        documento_id: f.id,
        eje,
        via: "banco",
        origen_tipo: "movimiento_bancario",
        origen_id: movimiento.id,
        cuota_id: a.cuota_id,
        monto_origen: conv.monto_origen,
        moneda_origen: conv.moneda_origen,
        tc: conv.tc,
        fecha_tc: conv.fecha_tc,
        monto_documento: a.montoDoc,
        fecha_evento: movimiento.fecha,
        monto: conv.monto_origen, // banco: positivo (paga)
        created_by: usuarioId || null,
      });
    }
  }
  return { eventos, warnings };
};

// Eventos de una vinculación de facturas EXTERNAS (Fase 4). Solo pago COMPLETO (el handler bloquea
// parciales): un evento via='banco' por externa, monto_documento = monto_total (moneda del documento);
// moneda_origen = moneda del movimiento (el banco paga en local); monto_origen = la porción del débito
// prorrateada por monto_total (MISMA fórmula que la fila MBF del handler). Congela tc si el documento
// no está en la moneda del movimiento (externa USD pagada con banco CLP — 10.185).
export const construirEventosExterna = ({ movimiento, externasSeleccionadas = [], pais, usuarioId = null }) => {
  const local = monedaLocalDe(pais);
  const monedaMov = movimiento?.moneda || local;
  const montoBanco = Math.abs(Number(movimiento?.monto || 0));
  const totalFacturas = externasSeleccionadas.reduce((s, f) => s + Number(f?.monto_total || 0), 0);
  return externasSeleccionadas
    .filter((f) => f && f.id != null && Number(f.monto_total) > 0)
    .map((f) => {
      const montoTotal = Number(f.monto_total);
      const mbfMonto = totalFacturas > 0 ? montoBanco * (montoTotal / totalFacturas) : montoBanco;
      const monedaFac = f.moneda || local;
      const cross = monedaMov !== monedaFac;
      return {
        documento_tipo: "factura_externa",
        documento_id: f.id,
        eje: "pago",
        via: "banco",
        origen_tipo: "movimiento_bancario",
        origen_id: movimiento.id,
        cuota_id: null,
        monto_origen: mbfMonto,
        moneda_origen: monedaMov,
        tc: cross && montoTotal ? mbfMonto / montoTotal : null,
        fecha_tc: cross ? movimiento.fecha : null,
        monto_documento: montoTotal,
        fecha_evento: movimiento.fecha,
        monto: mbfMonto,
        created_by: usuarioId || null,
      };
    });
};

// Escribe los eventos al libro IDEMPOTENTE (borra los del MISMO origen y re-inserta) y BEST-EFFORT:
// si falla, NO rompe el write viejo (ya commiteado), pero loguea visible y devuelve el error para el
// reporte (10.177/10.170 — nunca silencioso). `origen` = { origen_tipo, origen_id } identifica la
// fuente del/los evento(s): un movimiento bancario (banco/vinculación, externas) o una operación de
// factoring. Pasar `eventos = []` hace solo el borrado (revertir: anular factoring, desvincular).
// `opts.soloDocumentos` = [{ documento_tipo, documento_id }] acota el BORRADO a ESOS documentos dentro del
// origen (desvincular POR FILA de un movimiento con varias facturas: no puede barrer los eventos de las
// otras). Sin la opción (default) borra TODOS los eventos del origen — contrato original intacto (anular
// factoring / desvincular completo lo usan con `eventos=[]` y sin `opts`). Con la opción presente pero
// VACÍA no borra nada (0 documentos desvinculados).
export const escribirEventosSaldo = async (supabaseClient, origen, eventos = [], opts = {}) => {
  const { soloDocumentos = null } = opts;
  try {
    if (!(soloDocumentos && soloDocumentos.length === 0)) {
      let del = supabaseClient
        .from("aplicaciones_saldo")
        .delete()
        .eq("origen_tipo", origen.origen_tipo)
        .eq("origen_id", origen.origen_id);
      if (soloDocumentos && soloDocumentos.length > 0) {
        // OR sobre pares (documento_tipo, documento_id) — PostgREST no tiene tupla-IN. Los pares salen
        // del MBF que se está desvinculando (tipos/ids controlados, no input de usuario).
        const orExpr = soloDocumentos
          .map((d) => `and(documento_tipo.eq.${d.documento_tipo},documento_id.eq.${d.documento_id})`)
          .join(",");
        del = del.or(orExpr);
      }
      const { error: errDel } = await del;
      if (errDel) throw errDel;
    }
    if (eventos.length > 0) {
      const { error: errIns } = await supabaseClient.from("aplicaciones_saldo").insert(eventos);
      if (errIns) throw errIns;
    }
    return { ok: true, error: null };
  } catch (e) {
    const msg = e?.message || String(e);
    console.error("[dual-write aplicaciones_saldo] FALLÓ (best-effort; la vinculación vieja NO se rompe):", msg);
    return { ok: false, error: msg };
  }
};

// Ejecuta la vinculación completa. Orden: (1) mov bancario → procesado + FK/ cuota escalar limpias;
// (2) por factura: MBF (monto aplicado) + cuotas tildadas a pagado/cobrado con movimiento_bancario_id
// + factura 'vinculada'; (3) caja consolidada (idempotente por movimiento) + FK caja en el banco.
// Idempotente por par (mov, factura) en MBF y por movimiento en caja. No traga errores (10.177).
//
// params:
//   movimiento           — fila de movimientos_bancarios (id, monto, moneda, fecha).
//   facturasSeleccionadas— [{ id, monto_total, monto_neto, monto_iva, folio, moneda,
//                             cliente_id|proveedor_id, razon_social_receptor|emisor,
//                             venta_tipo?, venta_ref_id?, venta_ref_index?,
//                             cuotasSeleccionadas?: [{id, monto_cuota}], montoAsignado? }]
//   esCobro              — true = cobro (emitidas), false = pago (recibidas).
//   pais, usuarioId
//   entidadId            — override de entidad (cliente/proveedor) resuelta en UI; default = la de f[0].
//   descripcionExtra     — sufijo para la glosa de la caja (propina/forzado breadcrumbs).
//   categoriaPagoOverride— override de categoria_pago de la caja (default cobro_factura/pago_proveedor).
export const ejecutarVinculacionFacturasCuotas = async ({
  movimiento,
  facturasSeleccionadas = [],
  esCobro,
  pais,
  usuarioId,
  entidadId = null,
  descripcionExtra = "",
  categoriaPagoOverride = null,
  supabaseClient = client,
}) => {
  if (!movimiento?.id) throw new Error("ejecutarVinculacionFacturasCuotas: movimiento sin id");
  if (facturasSeleccionadas.length === 0) throw new Error("ejecutarVinculacionFacturasCuotas: sin facturas");

  // FASE 1.1b — TODA la escritura pasa por la RPC transaccional `vincular_cobro_bancario` /
  // `vincular_pago_bancario` (una sola transacción; si la RPC lanza, esta función lanza y NO se escribió
  // nada — 10.177). Esta función MANTIENE su firma y su valor de retorno (los callers no cambian): sigue
  // computando en JS lo mismo que V1 (monto aplicado por factura, prorrateo neto/impuesto de la caja,
  // glosa, entidad resuelta, eventos del libro con TC congelado por `construirEventosSaldo` — 10.185) y
  // arma `p_facturas`/`p_opciones` con el formato de la cabecera de la migración. Ya NO escribe tablas
  // directo (MBF/caja/facturas/plan_*/vinculaciones/libro): eso vive en la RPC, con paridad exacta.
  const campoEntidad = esCobro ? "cliente_id" : "proveedor_id";
  const esSingle = facturasSeleccionadas.length === 1;
  const entidadResuelta = entidadId != null ? entidadId : facturasSeleccionadas[0][campoEntidad];
  const entidadNombre = esCobro
    ? facturasSeleccionadas[0].razon_social_receptor
    : facturasSeleccionadas[0].razon_social_emisor;

  // Eventos del libro (via='banco') con TC congelado — MISMO builder que V1 (10.1/10.185). Se agrupan
  // por documento (factura) para el payload de la RPC. `construirEventosSaldo` es puro (no escribe).
  const { eventos: eventosSaldo, warnings: warningsSaldo } = construirEventosSaldo({
    movimiento,
    facturasSeleccionadas,
    esCobro,
    pais,
    usuarioId,
  });
  warningsSaldo.forEach((w) => console.warn("[vincular · aplicaciones_saldo]", w));

  // p_facturas: una entrada por factura (monto aplicado + venta legacy + cuota_ids tildadas + sus
  // eventos del libro). `cuotasVinculadas` se acumula para el retorno (paridad con V1).
  let cuotasVinculadas = 0;
  const p_facturas = await Promise.all(
    facturasSeleccionadas.map(async (f) => {
      const cuotas = cuotasDe(f);
      cuotasVinculadas += cuotas.length;
      const eventos = eventosSaldo
        .filter((e) => e.documento_id === f.id)
        .map((e) => ({
          cuota_id: e.cuota_id,
          monto_documento: e.monto_documento,
          monto_origen: e.monto_origen,
          moneda_origen: e.moneda_origen,
          tc: e.tc,
          fecha_tc: e.fecha_tc,
        }));
      // L4: cartelería/hardware → resolver linea_id (el puente ahora apunta a la línea). Pauta/contratos → null.
      const esCart = f.venta_tipo === "negocios_carteleria" || f.venta_tipo === "negocios_carteleria_hw";
      const linea_id = esCart ? await getLineaIdPorPosicion(f.venta_ref_id, f.venta_tipo, f.venta_ref_index) : null;
      return {
        factura_id: f.id,
        monto_aplicado: montoAplicadoFactura(f),
        venta_tipo: f.venta_tipo ?? null,
        venta_ref_id: f.venta_ref_id ?? null,
        venta_ref_index: f.venta_ref_index ?? null,
        linea_id,
        cuota_ids: cuotas.map((c) => c.id),
        eventos,
      };
    })
  );

  const { neto, impuesto } = netoImpuestoConsolidado(facturasSeleccionadas);
  const folios = facturasSeleccionadas.map((f) => f.folio).join(", ");
  const p_opciones = {
    pais,
    categoria_pago: categoriaPagoOverride, // null → la RPC usa el default por eje (cobro_factura/pago_proveedor)
    descripcion: `${entidadNombre} | Folios ${folios}${descripcionExtra}`,
    entidad_id: entidadResuelta ?? null,
    entidad_nombre: entidadNombre,
    monto_neto: neto,
    monto_impuesto: impuesto,
  };

  const rpcName = esCobro ? "vincular_cobro_bancario" : "vincular_pago_bancario";
  const { data, error } = await supabaseClient.rpc(rpcName, {
    p_movimiento_id: movimiento.id,
    p_facturas,
    p_usuario_id: usuarioId || null,
    p_opciones,
  });
  if (error) throw error; // 10.177: la RPC es atómica — si lanza, no persistió ningún write.

  // Retorno con el MISMO shape que V1 (los callers leen movCajaId/fkEscalar), armado del jsonb de la RPC.
  return {
    movCajaId: data?.movimiento_caja_id ?? null,
    facturasVinculadas: facturasSeleccionadas.length,
    cuotasVinculadas,
    fkEscalar: esSingle,
    libro: {
      ok: true,
      error: null,
      eventos: Array.isArray(data?.eventos_saldo_ids) ? data.eventos_saldo_ids.length : 0,
      warnings: warningsSaldo,
    },
    mbfIds: data?.mbf_ids ?? [],
  };
};
